"use client";

import { useState } from "react";
import {
  CopyButton,
  ErrorNotice,
  Notice,
  PillGroup,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  CARD,
  LABEL,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  BitStrip,
  ErrorSection,
  FactsTable,
  FieldsTable,
  FormulaLine,
  kindLabel,
  NeighboursTable,
} from "@/components/tools/cs-tools/ieee-views";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  type Analysis,
  analyseDecimal,
  analysePattern,
  FORMATS,
  type FormatId,
  parsePatternText,
} from "@/lib/tool-kit/cs-tools/ieee754";

/** English strings. A translated content file overrides these through `ui`. */
export const IEEE_754_CONVERTER_STRINGS = {
  formatLabel: "Format",
  formatHalf: "Half precision (binary16)",
  formatSingle: "Single precision (binary32)",
  formatDouble: "Double precision (binary64)",
  inputLabel: "What are you entering?",
  inputDecimal: "A decimal number",
  inputHex: "A hex bit pattern",
  inputBinary: "A binary bit pattern",
  decimalLabel: "Decimal value",
  decimalHint:
    "Any decimal such as 0.1, -118.625 or 6.02e23, or the words Infinity and NaN. It is converted exactly and rounded to nearest, ties to even.",
  decimalDefault: "0.1",
  hexLabel: "Hex pattern",
  hexHint:
    "Up to {digits} hex digits for this format, with an optional 0x. Shorter input is padded with zeros on the left.",
  binaryLabel: "Binary pattern",
  binaryHint:
    "Up to {bits} bits, with any spacing. Shorter input is padded with zeros on the left.",
  errEmpty: "Enter a value to convert.",
  errTooLong:
    "That is too long to convert. Shorten it to a few thousand characters.",
  errInvalidDecimal:
    "That is not a number. Use digits with an optional sign, decimal point and exponent, such as 1.5e-3.",
  errInvalidHex:
    "A hex pattern can contain only the digits 0–9 and the letters A–F.",
  errInvalidBinary: "A binary pattern can contain only the digits 0 and 1.",
  errTooWide:
    "That pattern is longer than {bits} bits, the size of this format.",
  resultsHeading: "Result",
  layoutHeading: "Bit layout",
  layoutLegend: "Sign, exponent and fraction fields",
  statHex: "Hexadecimal",
  statBits: "Bits",
  statKind: "Kind",
  statStored: "Stored value (shortest form that reads back the same)",
  statExponent: "Unbiased exponent",
  statNotApplicable: "None",
  kindZero: "Zero",
  kindSubnormal: "Subnormal number",
  kindNormal: "Normal number",
  kindInfinity: "Infinity",
  kindQuietNan: "Quiet NaN",
  kindSignallingNan: "Signalling NaN",
  infinitySymbol: "∞",
  nanToken: "NaN",
  fieldsCaption: "The three fields of the pattern",
  colField: "Field",
  colBits: "Bits",
  colMeaning: "Meaning",
  colQuantity: "Quantity",
  colValue: "Value",
  colPosition: "Neighbour",
  colHex: "Hex",
  rowSign: "Sign",
  rowExponent: "Exponent",
  rowFraction: "Fraction",
  signPositive: "0 means positive",
  signNegative: "1 means negative",
  exponentNormal: "Stored as {raw}; minus the bias of {bias} gives {unbiased}",
  exponentSubnormal:
    "Stored as 0: a subnormal number, which uses the smallest exponent ({min}) and has no hidden leading 1",
  exponentZero: "Stored as 0 with an all-zero fraction: zero",
  exponentAllOnes: "All ones: infinity if the fraction is zero, otherwise NaN",
  fractionSignificand: "Significand with the hidden bit: {significand}",
  fractionNan: "{kind}, payload {payload}",
  fractionNone: "All zeros",
  formulaHeading: "How the value is built",
  formulaLine: "(−1){sign} × 2{exponent} × {significand}",
  exactHeading: "Exact value of the stored number",
  exactBody:
    "Every binary float is exactly a finite decimal. This is the whole expansion — what the bits really hold, not a rounded display.",
  copyHex: "Copy hex",
  copyBits: "Copy bits",
  copyExact: "Copy exact value",
  copied: "Copied!",
  errorHeading: "Rounding error",
  errorCaption: "How far the stored value is from what you typed",
  errorEntered: "You entered",
  errorStored: "Nearest {format} value",
  errorAbsolute: "Error (stored − entered)",
  errorRelative: "Relative error",
  errorUlp: "Error as a fraction of one ulp",
  errorNone:
    "None. The value you entered is exactly representable in {format}, so nothing was rounded.",
  ulpNote:
    "An ulp is the gap between this float and the next one. Rounding to nearest never misses by more than half of it.",
  overflowNote:
    "The value is too large for this format, so it rounds to infinity. Anything at or above the largest finite value plus half an ulp does.",
  underflowNote:
    "The value is too small for this format, so it rounds to zero. Anything below half the smallest subnormal does.",
  subnormalNote:
    "This is a subnormal number. It has fewer significant bits than a normal one, so its relative precision is reduced.",
  neighboursHeading: "Neighbouring floats",
  neighboursCaption: "The floats just below and just above this one",
  nbDown: "Next lower",
  nbThis: "This value",
  nbUp: "Next higher",
  none: "None",
  factsHeading: "Facts about {format}",
  factsCaption: "Limits of the format",
  factLayout: "Layout",
  factLayoutValue:
    "1 sign + {exponent} exponent + {fraction} fraction = {total} bits",
  factBias: "Exponent bias",
  factRange: "Exponent range of normal numbers",
  factRangeValue: "{min} to {max}",
  factSubnormal: "Smallest positive subnormal",
  factNormal: "Smallest positive normal",
  factLargest: "Largest finite value",
  factEpsilon: "Machine epsilon (gap above 1)",
  factDigits: "Decimal digits always preserved",
  factRoundTrip: "Digits needed to round-trip any float",
  enterPrompt: "Enter a value to see its bit pattern.",
} as const;

type Strings = typeof IEEE_754_CONVERTER_STRINGS;
type InputKind = "decimal" | "hex" | "binary";

const FORMAT_LABEL: Record<FormatId, keyof Strings> = {
  half: "formatHalf",
  single: "formatSingle",
  double: "formatDouble",
};

type ErrorKey =
  | "errEmpty"
  | "errTooLong"
  | "errInvalidDecimal"
  | "errInvalidHex"
  | "errInvalidBinary"
  | "errTooWide";

type Outcome =
  | { kind: "error"; key: ErrorKey }
  | { kind: "ok"; analysis: Analysis };

function evaluate(input: InputKind, text: string, format: FormatId): Outcome {
  const f = FORMATS[format];
  if (input === "decimal") {
    const result = analyseDecimal(text, f);
    if (result.ok) {
      return { kind: "ok", analysis: result.analysis };
    }
    const keys = {
      empty: "errEmpty",
      tooLong: "errTooLong",
      invalid: "errInvalidDecimal",
    } as const;
    return { kind: "error", key: keys[result.error] };
  }
  const parsed = parsePatternText(text, input, f);
  if (parsed.ok) {
    return { kind: "ok", analysis: analysePattern(parsed.pattern, f) };
  }
  if (parsed.error === "empty") {
    return { kind: "error", key: "errEmpty" };
  }
  if (parsed.error === "tooWide") {
    return { kind: "error", key: "errTooWide" };
  }
  return {
    kind: "error",
    key: input === "hex" ? "errInvalidHex" : "errInvalidBinary",
  };
}

function textFor(kind: InputKind, a: Analysis, strings: Strings): string {
  if (kind === "decimal" && a.kind === "nan") {
    return strings.nanToken;
  }
  if (kind === "decimal" && a.kind === "infinity") {
    return `${a.fields.sign === 1 ? "-" : ""}${strings.infinitySymbol}`;
  }
  if (kind === "hex") {
    return a.hex;
  }
  if (kind === "binary") {
    return a.bits;
  }
  return (a.shortest ?? "").replace("−", "-");
}

export default function Ieee754ConverterWidget() {
  const strings = useToolUi(IEEE_754_CONVERTER_STRINGS);
  const fmt = useToolNumber();
  const [format, setFormat] = useState<FormatId>("single");
  const [input, setInput] = useState<InputKind>("decimal");
  const [text, setText] = useState<string>(strings.decimalDefault);

  const f = FORMATS[format];
  const outcome = evaluate(input, text, format);
  const analysis = outcome.kind === "ok" ? outcome.analysis : null;

  function changeInput(next: InputKind) {
    if (analysis) {
      setText(textFor(next, analysis, strings));
    }
    setInput(next);
  }

  const labels = {
    decimal: [strings.decimalLabel, strings.decimalHint],
    hex: [
      strings.hexLabel,
      t(strings.hexHint, { digits: fmt(f.total / 4, { useGrouping: false }) }),
    ],
    binary: [
      strings.binaryLabel,
      t(strings.binaryHint, { bits: fmt(f.total, { useGrouping: false }) }),
    ],
  } as const;

  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-2">
        <PillGroup
          label={strings.formatLabel}
          name="ieee-format"
          onChange={setFormat}
          options={(Object.keys(FORMATS) as FormatId[]).map((id) => ({
            value: id,
            label: strings[FORMAT_LABEL[id]],
          }))}
          value={format}
        />
        <PillGroup
          label={strings.inputLabel}
          name="ieee-input"
          onChange={changeInput}
          options={[
            { value: "decimal", label: strings.inputDecimal },
            { value: "hex", label: strings.inputHex },
            { value: "binary", label: strings.inputBinary },
          ]}
          value={input}
        />
      </div>
      <TextField
        hint={labels[input][1]}
        id="ieee-value"
        label={labels[input][0]}
        mono
        onChange={setText}
        value={text}
      />

      <div aria-live="polite" className="space-y-5">
        {outcome.kind === "error" ? (
          <ErrorNotice>
            {t(strings[outcome.key], {
              bits: fmt(f.total, { useGrouping: false }),
            })}
          </ErrorNotice>
        ) : null}
        {analysis ? (
          <Result
            analysis={analysis}
            enteredText={input === "decimal" ? text.trim() : ""}
            fmt={fmt}
            strings={strings}
          />
        ) : null}
        {outcome.kind === "error" && text.trim() === "" ? (
          <Notice>{strings.enterPrompt}</Notice>
        ) : null}
      </div>
      <FactsTable f={f} fmt={fmt} strings={strings} />
    </div>
  );
}

function Result({
  analysis: a,
  enteredText,
  strings,
  fmt,
}: {
  analysis: Analysis;
  enteredText: string;
  strings: Strings;
  fmt: ReturnType<typeof useToolNumber>;
}) {
  return (
    <>
      <h2 className={WIDGET_H2}>{strings.resultsHeading}</h2>
      <section className={`${CARD} space-y-3`}>
        <h3 className={WIDGET_H3}>{strings.layoutHeading}</h3>
        <p className={LABEL}>{strings.layoutLegend}</p>
        <BitStrip a={a} />
        <FieldsTable a={a} fmt={fmt} strings={strings} />
      </section>
      <StatGrid>
        <StatCard emphasis label={strings.statHex} value={a.hex} />
        <StatCard label={strings.statKind} value={kindLabel(a, strings)} />
        <StatCard
          label={strings.statStored}
          value={a.shortest ?? strings.statNotApplicable}
        />
        <StatCard
          label={strings.statExponent}
          value={
            a.exponent === null
              ? strings.statNotApplicable
              : fmt(a.exponent, { useGrouping: false })
          }
        />
      </StatGrid>
      <div className="flex flex-wrap gap-3">
        <CopyButton
          copiedLabel={strings.copied}
          label={strings.copyHex}
          text={a.hex}
        />
        <CopyButton
          copiedLabel={strings.copied}
          label={strings.copyBits}
          text={a.bits}
        />
      </div>
      <FormulaSection a={a} strings={strings} />
      <ErrorSection a={a} enteredText={enteredText} strings={strings} />
      {a.exact ? (
        <section className="space-y-3">
          <h3 className={WIDGET_H3}>{strings.exactHeading}</h3>
          <p className="text-muted-foreground text-sm">{strings.exactBody}</p>
          <p className="max-h-40 overflow-y-auto break-all rounded-lg border border-border bg-card p-3 font-mono text-foreground text-sm">
            {a.exact}
          </p>
          <CopyButton
            copiedLabel={strings.copied}
            label={strings.copyExact}
            text={a.exact.replace("−", "-")}
          />
        </section>
      ) : null}
      <NeighboursTable a={a} strings={strings} />
    </>
  );
}

function FormulaSection({ a, strings }: { a: Analysis; strings: Strings }) {
  if (a.exponent === null) {
    return null;
  }
  return (
    <section className="space-y-2">
      <h3 className={WIDGET_H3}>{strings.formulaHeading}</h3>
      <FormulaLine a={a} strings={strings} />
    </section>
  );
}
